import { useState, useEffect, useCallback } from 'react'
import JSZip from 'jszip'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Rocket, Trash2, AlertTriangle, CheckCircle2, Clock, XCircle, Download, Shield, Terminal, ChevronDown, ChevronUp, Wrench, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { useAuthStore } from '@/app/store/auth'
import { iacApi, type IaCDeployment, type IaCCredentialInfo } from '../api'

interface DeploymentPanelProps {
    projectId: number | null
    cloudPlatform: string
    files: Array<{ filename: string; code: string }>
    mainId: string
    scenario?: number
    hierarchyContext?: { serviceName?: string; objectCount?: number; versionCount?: number; hierarchyData?: any[] } | null
    onAskAgentToFix?: (errorMessage: string) => void
    verified?: boolean
    /** Reports the current deployment status up to the parent (e.g. to gate Clara). */
    onStatusChange?: (status: string) => void
    /** Fired when the user downloads the Terraform project. */
    onDownloaded?: () => void
    /** True while the agent is still generating/verifying — deploy & download stay disabled. */
    busy?: boolean
}

function StatusBadge({ status }: { status: string }) {
    const config: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: typeof CheckCircle2; color?: string }> = {
        not_deployed: { label: 'Not Deployed', variant: 'secondary', icon: Clock },
        pending: { label: 'Pending', variant: 'outline', icon: Clock },
        planning: { label: 'Initializing...', variant: 'outline', icon: Loader2, color: 'text-blue-500' },
        planned: { label: 'Plan Ready', variant: 'default', icon: CheckCircle2, color: 'text-green-500' },
        deploying: { label: 'Deploying...', variant: 'outline', icon: Rocket, color: 'text-blue-500' },
        deployed: { label: 'Deployed', variant: 'default', icon: CheckCircle2, color: 'text-green-500' },
        destroying: { label: 'Destroying...', variant: 'outline', icon: Trash2, color: 'text-orange-500' },
        destroyed: { label: 'Destroyed', variant: 'secondary', icon: XCircle },
        failed: { label: 'Failed', variant: 'destructive', icon: XCircle },
    }
    const c = config[status] || config.not_deployed
    const Icon = c.icon
    const isAnimated = ['planning', 'deploying', 'destroying', 'pending'].includes(status)
    return (
        <Badge variant={c.variant} className="gap-1">
            <Icon className={`h-3 w-3 ${isAnimated ? 'animate-spin' : ''} ${c.color || ''}`} />{c.label}
        </Badge>
    )
}

function ProgressTimeline({ deployment }: { deployment: IaCDeployment }) {
    const stages = [
        { key: 'init', label: 'Initialize', statuses: ['planning'] },
        { key: 'plan', label: 'Plan', statuses: ['planned'] },
        { key: 'apply', label: 'Deploy', statuses: ['deploying', 'deployed'] },
    ]

    const statusOrder = ['pending', 'planning', 'planned', 'deploying', 'deployed']
    const currentIdx = statusOrder.indexOf(deployment.status)

    return (
        <div className="flex items-center gap-1 mt-2">
            {stages.map((stage, i) => {
                const stageStartIdx = i === 0 ? 1 : i === 1 ? 2 : 3
                const isComplete = currentIdx > stageStartIdx
                const isActive = currentIdx === stageStartIdx
                const isFailed = deployment.status === 'failed'

                return (
                    <div key={stage.key} className="flex items-center gap-1 flex-1">
                        <div className={`flex items-center justify-center h-6 w-6 rounded-full text-[10px] font-semibold flex-shrink-0
                            ${isComplete ? 'bg-green-500 text-white' : isActive ? (isFailed ? 'bg-red-500 text-white' : 'bg-blue-500 text-white animate-pulse') : 'bg-muted text-muted-foreground'}`}>
                            {isComplete ? '✓' : i + 1}
                        </div>
                        <span className={`text-[10px] ${isActive ? 'font-semibold' : 'text-muted-foreground'}`}>{stage.label}</span>
                        {i < stages.length - 1 && (
                            <div className={`flex-1 h-0.5 mx-1 ${isComplete ? 'bg-green-500' : 'bg-muted'}`} />
                        )}
                    </div>
                )
            })}
        </div>
    )
}

export function DeploymentPanel({ projectId, cloudPlatform, files, mainId, scenario, hierarchyContext, onAskAgentToFix, verified = false, onStatusChange, onDownloaded, busy = false }: DeploymentPanelProps) {
    const { toast } = useToast()
    const queryClient = useQueryClient()
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

    // Detect provider from the platform or files content — must be before state that references it
    const detectedProvider = (() => {
        if (cloudPlatform && cloudPlatform !== 'multi') return cloudPlatform
        const allCode = files.map(f => f.code).join('\n')
        if (allCode.includes('provider "azurerm"') || allCode.includes('azurerm_')) return 'azure'
        if (allCode.includes('provider "google"') || allCode.includes('google_')) return 'gcp'
        return 'aws'
    })()

    const [showCredentials, setShowCredentials] = useState(false)
    const [selectedProvider, setSelectedProvider] = useState<string>(detectedProvider)
    const [showConfirmDestroy, setShowConfirmDestroy] = useState(false)
    const [showLogs, setShowLogs] = useState(false)
    const [credForm, setCredForm] = useState<Record<string, string>>({})

    const provider = selectedProvider

    // Check if credentials exist
    const { data: credData } = useQuery({
        queryKey: ['iac-credentials', provider],
        queryFn: async () => {
            const res = await iacApi.checkCredentials(provider)
            const d = res.data as any
            return (d?.data || d) as IaCCredentialInfo
        },
        enabled: isAuthenticated && !!provider,
    })

    // Get deployment status
    const { data: deployData } = useQuery({
        queryKey: ['iac-deployment', projectId],
        queryFn: async () => {
            if (!projectId) return null
            const res = await iacApi.getDeployment(projectId)
            const d = res.data as any
            return (d?.data || d) as IaCDeployment | null
        },
        enabled: isAuthenticated && !!projectId,
        refetchInterval: (query) => {
            const status = query.state.data?.status
            // Poll more frequently during active operations
            if (status === 'planning' || status === 'deploying' || status === 'destroying') return 3000
            if (status === 'pending' || status === 'planned') return 5000
            return false
        },
    })

    const deployment = deployData || null
    const hasCredentials = credData?.exists && credData?.configured
    const isDeployed = deployment?.status === 'deployed'
    const isFailed = deployment?.status === 'failed'
    const isActive = ['planning', 'deploying', 'destroying', 'pending', 'planned'].includes(deployment?.status || '')
    // A real, meaningful deployment exists (not a no-deployment/error response and
    // not the idle "not_deployed" default) — only then is the full status panel useful.
    const hasRealDeployment = ['pending', 'planning', 'planned', 'deploying', 'deployed', 'destroying', 'destroyed', 'failed'].includes(deployment?.status || '')

    // Report status changes up (used to gate Clara availability on the chat side).
    useEffect(() => {
        onStatusChange?.(deployment?.status || 'not_deployed')
    }, [deployment?.status, onStatusChange])

    // Auto-show logs when deployment fails
    useEffect(() => {
        if (isFailed) setShowLogs(true)
    }, [isFailed])

    // Save credentials — pass all form fields, backend stores the full object in Vault
    const saveCredsMutation = useMutation({
        mutationFn: () => iacApi.saveCredentials(selectedProvider, credForm as any),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['iac-credentials', selectedProvider] })
            setShowCredentials(false)
            toast({ title: 'Credentials saved securely in Vault' })
        },
        onError: () => toast({ title: 'Failed to save credentials', variant: 'destructive' }),
    })

    // Create deployment record and download smart script
    const createDeployMutation = useMutation({
        mutationFn: async () => {
            const res = await iacApi.createDeployment({
                projectId: projectId!,
                cloudProvider: provider,
                region: credData?.region || credForm.defaultRegion || undefined,
            })
            const d = res.data as any
            const dep = d?.data || d
            return dep as IaCDeployment
        },
        onSuccess: (dep) => {
            queryClient.invalidateQueries({ queryKey: ['iac-deployment', projectId] })
            setShowCredentials(false)
            // Download the project with the smart deploy script
            downloadSmartProject(dep)
            toast({ title: 'Deployment initiated', description: 'Extract and run deploy.sh — progress will be reported back here.' })
        },
        onError: () => toast({ title: 'Failed to create deployment', variant: 'destructive' }),
    })

    // Update deployment to destroyed
    const destroyMutation = useMutation({
        mutationFn: () => {
            if (!deployment?.id) throw new Error('No deployment')
            return iacApi.updateDeploymentStatus(deployment.id, { status: 'destroying', statusMessage: 'Destroy initiated — run destroy.sh on your device' })
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['iac-deployment', projectId] })
            setShowConfirmDestroy(false)
            // Download the project with destroy script
            if (deployment) downloadDestroyScript(deployment)
            toast({ title: 'Run destroy.sh to remove cloud resources' })
        },
    })

    // Generate the smart deploy script that reports progress back
    function generateSmartDeployScript(dep: IaCDeployment): string {
        const baseUrl = window.location.origin
        const deployToken = dep.deployToken || ''
        const lines = [
            '#!/bin/bash',
            '# ═══════════════════════════════════════════════════════════',
            '# VaultSentinel — Smart Terraform Deployment Script',
            '# Project: ' + mainId,
            '# Deployment ID: ' + dep.id,
            '# Generated: ' + new Date().toISOString(),
            '# ═══════════════════════════════════════════════════════════',
            'set -euo pipefail',
            '',
            '# ── Configuration ──',
            'VAULTSENTINEL_URL="' + baseUrl + '"',
            'DEPLOYMENT_ID="' + dep.id + '"',
            'DEPLOY_TOKEN="' + deployToken + '"',
            '',
            '# ── Helpers ──',
            'report_progress() {',
            '    local stage="$1" status="$2" message="$3" output="${4:-}" error_output="${5:-}"',
            '    # JSON-encode the output fields',
            '    local json_output json_error',
            '    json_output=$(echo -n "$output" | python3 -c \'import sys,json; print(json.dumps(sys.stdin.read()))\' 2>/dev/null || echo \'""\')',
            '    json_error=$(echo -n "$error_output" | python3 -c \'import sys,json; print(json.dumps(sys.stdin.read()))\' 2>/dev/null || echo \'""\')',
            '    curl -s -X PUT "$VAULTSENTINEL_URL/api/iac/deployments/$DEPLOYMENT_ID/progress" \\',
            '        -H "Content-Type: application/json" \\',
            '        -H "x-deploy-token: $DEPLOY_TOKEN" \\',
            '        -d "{\\"stage\\": \\"$stage\\", \\"status\\": \\"$status\\", \\"message\\": \\"$message\\", \\"output\\": $json_output, \\"errorOutput\\": $json_error}" \\',
            '        > /dev/null 2>&1 || true',
            '}',
            '',
            'cleanup_on_error() {',
            '    local exit_code=$?',
            '    if [ $exit_code -ne 0 ] && [ "${STAGE:-}" != "" ]; then',
            '        report_progress "$STAGE" "error" "Script failed with exit code $exit_code" "" "${ERROR_LOG:-Unknown error}"',
            '    fi',
            '}',
            'trap cleanup_on_error EXIT',
            '',
            'echo ""',
            'echo "╔══════════════════════════════════════════════════════════╗"',
            'echo "║  VaultSentinel — Terraform Test Deployment               ║"',
            'echo "║  Progress is reported back to VaultSentinel in real-time ║"',
            'echo "╚══════════════════════════════════════════════════════════╝"',
            'echo ""',
            '',
            '# ── Check prerequisites ──',
            'if ! command -v terraform &> /dev/null; then',
            '    report_progress "error" "error" "Terraform is not installed" "" "Terraform CLI not found. Install from https://terraform.io"',
            '    echo "ERROR: Terraform is not installed. Install from https://terraform.io"',
            '    exit 1',
            'fi',
            '',
            'if ! command -v curl &> /dev/null; then',
            '    echo "ERROR: curl is required for progress reporting"',
            '    exit 1',
            'fi',
            '',
            'TF_VER=$(terraform version | head -1)',
            'echo "Terraform: $TF_VER"',
            'echo ""',
            '',
            '# ── Fetch credentials from VaultSentinel ──',
            'STAGE="credentials"',
            'echo "═══ Fetching credentials from VaultSentinel Vault ═══"',
            'report_progress "credentials" "started" "Fetching cloud credentials from Vault..."',
            '',
            'CREDS_RESPONSE=$(curl -sf "$VAULTSENTINEL_URL/api/iac/deployments/$DEPLOYMENT_ID/credentials" \\',
            '    -H "x-deploy-token: $DEPLOY_TOKEN" 2>/dev/null || echo "")',
            '',
            'CLOUD_PROVIDER=""',
            'if [ -n "$CREDS_RESPONSE" ]; then',
            '    CLOUD_PROVIDER=$(echo "$CREDS_RESPONSE" | python3 -c \'import sys,json; print(json.loads(sys.stdin.read())["data"].get("provider",""))\' 2>/dev/null || echo "")',
            'fi',
            '',
            'if [ "$CLOUD_PROVIDER" = "azure" ]; then',
            '    # Azure Service Principal credentials',
            '    export ARM_CLIENT_ID=$(echo "$CREDS_RESPONSE" | python3 -c \'import sys,json; print(json.loads(sys.stdin.read())["data"]["clientId"])\' 2>/dev/null || echo "")',
            '    export ARM_CLIENT_SECRET=$(echo "$CREDS_RESPONSE" | python3 -c \'import sys,json; print(json.loads(sys.stdin.read())["data"]["clientSecret"])\' 2>/dev/null || echo "")',
            '    export ARM_TENANT_ID=$(echo "$CREDS_RESPONSE" | python3 -c \'import sys,json; print(json.loads(sys.stdin.read())["data"]["tenantId"])\' 2>/dev/null || echo "")',
            '    export ARM_SUBSCRIPTION_ID=$(echo "$CREDS_RESPONSE" | python3 -c \'import sys,json; print(json.loads(sys.stdin.read())["data"]["subscriptionId"])\' 2>/dev/null || echo "")',
            '    if [ -n "$ARM_CLIENT_ID" ] && [ -n "$ARM_CLIENT_SECRET" ]; then',
            '        echo "✓ Azure Service Principal credentials loaded from Vault"',
            '    else',
            '        echo "⚠ Azure credentials incomplete. Set ARM_CLIENT_ID, ARM_CLIENT_SECRET, ARM_TENANT_ID, ARM_SUBSCRIPTION_ID"',
            '    fi',
            'elif [ "$CLOUD_PROVIDER" = "gcp" ]; then',
            '    # GCP Service Account credentials',
            '    GCP_KEY=$(echo "$CREDS_RESPONSE" | python3 -c \'import sys,json; print(json.loads(sys.stdin.read())["data"]["serviceAccountKey"])\' 2>/dev/null || echo "")',
            '    if [ -n "$GCP_KEY" ]; then',
            '        echo "$GCP_KEY" > /tmp/gcp-key-$DEPLOYMENT_ID.json',
            '        export GOOGLE_APPLICATION_CREDENTIALS="/tmp/gcp-key-$DEPLOYMENT_ID.json"',
            '        export GOOGLE_PROJECT=$(echo "$CREDS_RESPONSE" | python3 -c \'import sys,json; print(json.loads(sys.stdin.read())["data"]["projectId"])\' 2>/dev/null || echo "")',
            '        REGION=$(echo "$CREDS_RESPONSE" | python3 -c \'import sys,json; print(json.loads(sys.stdin.read())["data"].get("defaultRegion") or "")\' 2>/dev/null || echo "")',
            '        if [ -n "$REGION" ]; then export GOOGLE_REGION="$REGION"; fi',
            '        echo "✓ GCP Service Account credentials loaded from Vault"',
            '    else',
            '        echo "⚠ GCP credentials incomplete. Set GOOGLE_APPLICATION_CREDENTIALS"',
            '    fi',
            'elif [ -n "$CREDS_RESPONSE" ]; then',
            '    # AWS credentials (default)',
            '    export AWS_ACCESS_KEY_ID=$(echo "$CREDS_RESPONSE" | python3 -c \'import sys,json; print(json.loads(sys.stdin.read())["data"]["accessKeyId"])\' 2>/dev/null || echo "")',
            '    export AWS_SECRET_ACCESS_KEY=$(echo "$CREDS_RESPONSE" | python3 -c \'import sys,json; print(json.loads(sys.stdin.read())["data"]["secretAccessKey"])\' 2>/dev/null || echo "")',
            '    REGION=$(echo "$CREDS_RESPONSE" | python3 -c \'import sys,json; print(json.loads(sys.stdin.read())["data"].get("defaultRegion") or "")\' 2>/dev/null || echo "")',
            '    if [ -n "$REGION" ]; then export AWS_DEFAULT_REGION="$REGION"; fi',
            '    if [ -n "$AWS_ACCESS_KEY_ID" ]; then',
            '        echo "✓ AWS credentials loaded from VaultSentinel Vault"',
            '    else',
            '        echo "⚠ Could not extract AWS credentials from Vault response."',
            '    fi',
            'else',
            '    echo "⚠ Could not fetch credentials from Vault. Using environment variables."',
            'fi',
            '',
            '# Fallback: prompt for credentials if none are set',
            'if [ -z "${ARM_CLIENT_ID:-}" ] && [ -z "${AWS_ACCESS_KEY_ID:-}" ] && [ -z "${GOOGLE_APPLICATION_CREDENTIALS:-}" ]; then',
            '    echo ""',
            '    echo "No cloud credentials detected. Please set the appropriate environment variables:"',
            '    echo "  AWS:   AWS_ACCESS_KEY_ID, AWS_SECRET_ACCESS_KEY"',
            '    echo "  Azure: ARM_CLIENT_ID, ARM_CLIENT_SECRET, ARM_TENANT_ID, ARM_SUBSCRIPTION_ID"',
            '    echo "  GCP:   GOOGLE_APPLICATION_CREDENTIALS (path to JSON key)"',
            '    echo ""',
            '    echo "Or store them in VaultSentinel and re-run."',
            '    exit 1',
            'fi',
            '',
            'echo ""',
            '',
            '# ── Initialize Terraform ──',
            'STAGE="init"',
            'echo "═══ Step 1/3: Initializing Terraform ═══"',
            'report_progress "init" "started" "Running terraform init..."',
            '',
            'INIT_OUTPUT=""',
            'if INIT_OUTPUT=$(terraform init -no-color 2>&1); then',
            '    echo "✓ Terraform initialized"',
            '    report_progress "init" "success" "Terraform initialized successfully"',
            'else',
            '    echo "✗ Terraform init failed"',
            '    echo "$INIT_OUTPUT"',
            '    report_progress "init" "error" "Terraform init failed" "" "$INIT_OUTPUT"',
            '    ERROR_LOG="$INIT_OUTPUT"',
            '    exit 1',
            'fi',
            '',
            'echo ""',
            '',
            '# ── Plan ──',
            'STAGE="plan"',
            'echo "═══ Step 2/3: Planning Deployment ═══"',
            'report_progress "plan" "started" "Running terraform plan..."',
            '',
            'PLAN_OUTPUT=""',
            'if PLAN_OUTPUT=$(terraform plan -no-color -out=tfplan 2>&1); then',
            '    echo "✓ Terraform plan complete"',
            '    echo ""',
            '    echo "$PLAN_OUTPUT" | tail -20',
            '    report_progress "plan" "success" "Terraform plan complete" "$PLAN_OUTPUT"',
            'else',
            '    echo "✗ Terraform plan failed"',
            '    echo ""',
            '    echo "$PLAN_OUTPUT"',
            '    report_progress "plan" "error" "Terraform plan failed" "" "$PLAN_OUTPUT"',
            '    ERROR_LOG="$PLAN_OUTPUT"',
            '    exit 1',
            'fi',
            '',
            'echo ""',
            '',
            '# ── Apply ──',
            'STAGE="apply"',
            'echo "═══ Step 3/3: Applying Terraform ═══"',
            'read -p "Review the plan above. Apply changes? (yes/no): " CONFIRM',
            'if [ "$CONFIRM" != "yes" ]; then',
            '    report_progress "apply" "error" "Deployment cancelled by user" "" "User declined to apply the plan"',
            '    echo "Deployment cancelled."',
            '    exit 0',
            'fi',
            '',
            'echo ""',
            'echo "Applying..."',
            'report_progress "apply" "started" "Running terraform apply..."',
            '',
            'APPLY_OUTPUT=""',
            'if APPLY_OUTPUT=$(terraform apply -no-color tfplan 2>&1); then',
            '    echo "✓ Terraform apply complete!"',
            '    echo ""',
            '    report_progress "apply" "success" "Deployment successful" "$APPLY_OUTPUT"',
            '    echo "$APPLY_OUTPUT" | tail -10',
            '    echo ""',
            '    echo "═══════════════════════════════════════════════════"',
            '    echo "  ✓ DEPLOYMENT SUCCESSFUL"',
            '    echo "  Check VaultSentinel for status and details."',
            '    echo ""',
            '    echo "  To destroy when done testing:"',
            '    echo "    bash destroy.sh"',
            '    echo "═══════════════════════════════════════════════════"',
            'else',
            '    echo "✗ Terraform apply failed"',
            '    echo ""',
            '    echo "$APPLY_OUTPUT"',
            '    report_progress "apply" "error" "Terraform apply failed" "" "$APPLY_OUTPUT"',
            '    ERROR_LOG="$APPLY_OUTPUT"',
            '    echo ""',
            '    echo "═══════════════════════════════════════════════════"',
            '    echo "  ✗ DEPLOYMENT FAILED"',
            '    echo "  The error has been reported to VaultSentinel."',
            '    echo "  You can ask the IaC Agent to analyse and fix it."',
            '    echo "═══════════════════════════════════════════════════"',
            '    exit 1',
            'fi',
        ]
        return lines.join('\n')
    }

    function generateSmartDestroyScript(dep: IaCDeployment): string {
        const baseUrl = window.location.origin
        const deployToken = dep.deployToken || ''
        const lines = [
            '#!/bin/bash',
            '# VaultSentinel — Terraform Destroy Script',
            '# Project: ' + mainId + ' | Deployment: ' + dep.id,
            'set -euo pipefail',
            '',
            'VAULTSENTINEL_URL="' + baseUrl + '"',
            'DEPLOYMENT_ID="' + dep.id + '"',
            'DEPLOY_TOKEN="' + deployToken + '"',
            '',
            'report_progress() {',
            '    local stage="$1" status="$2" message="$3" output="${4:-}" error_output="${5:-}"',
            '    local json_output json_error',
            '    json_output=$(echo -n "$output" | python3 -c \'import sys,json; print(json.dumps(sys.stdin.read()))\' 2>/dev/null || echo \'""\')',
            '    json_error=$(echo -n "$error_output" | python3 -c \'import sys,json; print(json.dumps(sys.stdin.read()))\' 2>/dev/null || echo \'""\')',
            '    curl -s -X PUT "$VAULTSENTINEL_URL/api/iac/deployments/$DEPLOYMENT_ID/progress" \\',
            '        -H "Content-Type: application/json" \\',
            '        -H "x-deploy-token: $DEPLOY_TOKEN" \\',
            '        -d "{\\"stage\\": \\"$stage\\", \\"status\\": \\"$status\\", \\"message\\": \\"$message\\", \\"output\\": $json_output, \\"errorOutput\\": $json_error}" \\',
            '        > /dev/null 2>&1 || true',
            '}',
            '',
            'echo ""',
            'echo "╔══════════════════════════════════════════════════════════╗"',
            'echo "║  VaultSentinel — Terraform DESTROY                       ║"',
            'echo "║  ⚠  This will remove ALL cloud resources                ║"',
            'echo "╚══════════════════════════════════════════════════════════╝"',
            'echo ""',
            '',
            'read -p "Are you sure you want to destroy all resources? (yes/no): " CONFIRM',
            'if [ "$CONFIRM" != "yes" ]; then',
            '    echo "Cancelled."',
            '    exit 0',
            'fi',
            '',
            'echo ""',
            'echo "Destroying resources..."',
            'report_progress "destroy" "started" "Destroying cloud resources..."',
            '',
            'DESTROY_OUTPUT=""',
            'if DESTROY_OUTPUT=$(terraform destroy -auto-approve -no-color 2>&1); then',
            '    echo "✓ All resources destroyed"',
            '    report_progress "destroy" "success" "All resources destroyed" "$DESTROY_OUTPUT"',
            '    echo ""',
            '    echo "═══════════════════════════════════════════════════"',
            '    echo "  ✓ RESOURCES DESTROYED"',
            '    echo "═══════════════════════════════════════════════════"',
            'else',
            '    echo "✗ Destroy failed"',
            '    echo "$DESTROY_OUTPUT"',
            '    report_progress "destroy" "error" "Terraform destroy failed" "" "$DESTROY_OUTPUT"',
            '    exit 1',
            'fi',
        ]
        return lines.join('\n')
    }

    async function downloadSmartProject(dep: IaCDeployment) {
        const allFiles = [
            ...files,
            { filename: 'deploy.sh', code: generateSmartDeployScript(dep) },
            { filename: 'destroy.sh', code: generateSmartDestroyScript(dep) },
        ]
        await downloadAsZip(allFiles, `${mainId}-terraform-project.zip`)
    }

    function downloadDestroyScript(dep: IaCDeployment) {
        const code = generateSmartDestroyScript(dep)
        const blob = new Blob([code], { type: 'text/plain' })
        const a = document.createElement('a')
        a.href = URL.createObjectURL(blob)
        a.download = `${mainId}-destroy.sh`
        a.click()
    }

    async function downloadProject() {
        await downloadAsZip(files, `${mainId}-terraform.zip`)
    }

    async function downloadAsZip(fileList: Array<{ filename: string; code: string }>, zipName: string) {
        const zip = new JSZip()
        const folder = zip.folder(mainId) || zip
        for (const f of fileList) {
            folder.file(f.filename, f.code || '')
        }
        const blob = await zip.generateAsync({ type: 'blob' })
        const a = document.createElement('a')
        a.href = URL.createObjectURL(blob)
        a.download = zipName
        a.click()
        URL.revokeObjectURL(a.href)
    }

    // Get the error message from the deployment for the "Ask Agent to Fix" button
    function getDeploymentError(): string {
        if (!deployment) return ''
        const parts: string[] = []
        if (deployment.statusMessage) parts.push(deployment.statusMessage)
        // Check if plan or apply output contains error details
        if (deployment.applyOutput && deployment.status === 'failed') {
            // Extract the last 100 lines of output
            const lines = deployment.applyOutput.split('\n')
            parts.push('--- Terraform Output ---')
            parts.push(lines.slice(-100).join('\n'))
        } else if (deployment.planOutput && deployment.status === 'failed') {
            const lines = deployment.planOutput.split('\n')
            parts.push('--- Terraform Output ---')
            parts.push(lines.slice(-100).join('\n'))
        }
        return parts.join('\n\n')
    }

    if (files.length === 0) return null

    return (
        <div className="space-y-3">
            {/* Before any real deployment activity, just a small status pill — the
                full panel (logs, timeline, destroy) has nothing to show yet, and the
                action buttons already convey the not-deployed state. */}
            {!hasRealDeployment && (
                <StatusBadge status="not_deployed" />
            )}
            {/* Full deployment status panel — shown once a deployment is underway/done. */}
            {deployment && hasRealDeployment && (
                <div className="rounded-lg border bg-muted/30 overflow-hidden">
                    <div className="flex items-center justify-between p-3">
                        <div className="flex items-center gap-3">
                            <StatusBadge status={deployment.status} />
                            {deployment.statusMessage && !isFailed && (
                                <span className="text-xs text-muted-foreground max-w-[300px] truncate">
                                    {deployment.statusMessage}
                                </span>
                            )}
                            {deployment.deployedAt && (
                                <span className="text-xs text-muted-foreground">
                                    Deployed: {new Date(deployment.deployedAt).toLocaleString()}
                                </span>
                            )}
                            {deployment.destroyedAt && (
                                <span className="text-xs text-muted-foreground">
                                    Destroyed: {new Date(deployment.destroyedAt).toLocaleString()}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            {/* Show logs button when there's output */}
                            {(deployment.planOutput || deployment.applyOutput || deployment.statusMessage) && (
                                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setShowLogs(!showLogs)}>
                                    <Terminal className="h-3 w-3 mr-1" />
                                    Logs
                                    {showLogs ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
                                </Button>
                            )}
                            {isDeployed && (
                                <Button variant="destructive" size="sm" className="h-7" onClick={() => setShowConfirmDestroy(true)}>
                                    <Trash2 className="h-3.5 w-3.5 mr-1.5" />Destroy
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Progress timeline for active deployments */}
                    {isActive && <div className="px-3 pb-3"><ProgressTimeline deployment={deployment} /></div>}

                    {/* Error display */}
                    {isFailed && (
                        <div className="border-t bg-red-50 dark:bg-red-950/30 p-3">
                            <div className="flex items-start gap-2 mb-2">
                                <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-red-700 dark:text-red-400">Deployment Failed</p>
                                    <p className="text-xs text-red-600 dark:text-red-400/80 mt-0.5 break-words whitespace-pre-wrap" style={{ maxHeight: '80px', overflow: 'auto' }}>
                                        {deployment.statusMessage || 'Unknown error'}
                                    </p>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                {onAskAgentToFix && (
                                    <Button size="sm" className="h-7 bg-blue-600 hover:bg-blue-700" onClick={() => onAskAgentToFix(getDeploymentError())}>
                                        <Wrench className="h-3.5 w-3.5 mr-1.5" />Ask Agent to Fix
                                    </Button>
                                )}
                                <Button variant="outline" size="sm" className="h-7" onClick={() => {
                                    // Reset and retry
                                    if (deployment?.id) {
                                        iacApi.updateDeploymentStatus(deployment.id, { status: 'not_deployed', statusMessage: 'Reset for retry' })
                                            .then(() => queryClient.invalidateQueries({ queryKey: ['iac-deployment', projectId] }))
                                    }
                                }}>
                                    Retry
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Collapsible logs */}
                    {showLogs && (deployment.planOutput || deployment.applyOutput || deployment.destroyOutput) && (
                        <div className="border-t">
                            <pre className="p-3 text-[10px] font-mono leading-relaxed bg-zinc-950 text-zinc-300 max-h-[200px] overflow-auto whitespace-pre-wrap">
                                {deployment.applyOutput || deployment.planOutput || deployment.destroyOutput || deployment.statusMessage}
                            </pre>
                        </div>
                    )}
                </div>
            )}

            {/* Action buttons */}
            <div className="flex items-center gap-2">
                {hasCredentials && (
                    <Badge variant="outline" className="text-xs text-green-600">
                        <Shield className="h-3 w-3 mr-1" />Credentials set ({credData?.accessKeyHint})
                    </Badge>
                )}
                <div className="flex-1" />
                {!verified && files.length > 0 && (
                    <Badge variant="outline" className="text-xs text-amber-600">
                        <Clock className="h-3 w-3 mr-1" />Awaiting verification
                    </Badge>
                )}
                {verified && (
                    <Badge variant="outline" className="text-xs text-green-600">
                        <CheckCircle2 className="h-3 w-3 mr-1" />Verified
                    </Badge>
                )}
                {!isDeployed && !isActive && (
                    <Button size="sm" onClick={() => setShowCredentials(true)} disabled={!verified || busy}>
                        <Rocket className="h-3.5 w-3.5 mr-1.5" />Test Deployment
                    </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => { downloadProject(); onDownloaded?.(); }} disabled={files.length === 0 || busy}>
                    <Download className="h-3.5 w-3.5 mr-1.5 text-blue-600" />Download
                </Button>
            </div>

            {/* Credentials dialog — provider-aware with full context */}
            {showCredentials && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-card rounded-lg border p-6 shadow-lg max-w-lg mx-4 w-full max-h-[90vh] overflow-y-auto">
                        <h3 className="text-lg font-semibold mb-2">Test Deployment — Enter Credentials</h3>

                        {/* Provider tabs */}
                        <div className="flex gap-1 mb-4 p-1 rounded-lg bg-muted">
                            {[
                                { key: 'aws', label: 'AWS', icon: '/images/foreground/icons/vsiconawsec2.png' },
                                { key: 'azure', label: 'Azure', icon: '/images/foreground/icons/vsiconwindows2019.png' },
                                { key: 'gcp', label: 'GCP', icon: '/images/foreground/icons/vsicongemini.png' },
                            ].map((p) => (
                                <button key={p.key}
                                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${selectedProvider === p.key ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                                    onClick={() => { setSelectedProvider(p.key); setCredForm({}) }}>
                                    {p.label}
                                </button>
                            ))}
                        </div>

                        {/* What these are for */}
                        <div className="text-sm text-muted-foreground mb-3">
                            <p>Enter your <strong>{selectedProvider === 'azure' ? 'Azure' : selectedProvider === 'gcp' ? 'Google Cloud' : 'AWS'}</strong> credentials for Terraform to authenticate and create recovery infrastructure. Credentials are passed to the deploy script and not stored permanently.</p>
                        </div>

                        <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 mb-4">
                            <AlertTriangle className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                            <span className="text-xs text-yellow-700 dark:text-yellow-400">
                                Use <strong>test/sandbox</strong> credentials only. Never use production account credentials for test deployments.
                            </span>
                        </div>

                        {/* Provider-specific instructions and fields */}
                        {selectedProvider === 'azure' ? (
                            <>
                                <div className="rounded-lg border bg-blue-50/50 dark:bg-blue-950/20 p-3 mb-4">
                                    <p className="text-xs font-medium mb-1">How to get these credentials:</p>
                                    <ol className="text-xs text-muted-foreground space-y-0.5 list-decimal ml-3">
                                        <li>Run the generated <code className="inline-code">setup-azure.sh</code> script</li>
                                        <li>It creates a Service Principal and outputs these 4 values</li>
                                        <li>Or create manually: Azure Portal → App registrations → New registration</li>
                                    </ol>
                                    <p className="text-xs text-muted-foreground mt-1.5">
                                        <strong>Required roles:</strong> Contributor, User Access Administrator, Key Vault Administrator
                                    </p>
                                </div>
                                <div className="space-y-3">
                                    <div>
                                        <Label className="text-xs">Client ID <span className="text-muted-foreground">(Service Principal Application ID)</span></Label>
                                        <Input value={credForm.clientId || ''} onChange={(e) => setCredForm(f => ({ ...f, clientId: e.target.value }))}
                                            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="font-mono text-sm" />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Client Secret <span className="text-muted-foreground">(Service Principal password)</span></Label>
                                        <Input type="password" value={credForm.clientSecret || ''} onChange={(e) => setCredForm(f => ({ ...f, clientSecret: e.target.value }))}
                                            placeholder="••••••••" className="font-mono text-sm" />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Tenant ID <span className="text-muted-foreground">(Azure AD directory ID)</span></Label>
                                        <Input value={credForm.tenantId || ''} onChange={(e) => setCredForm(f => ({ ...f, tenantId: e.target.value }))}
                                            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="font-mono text-sm" />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Subscription ID <span className="text-muted-foreground">(target Azure subscription)</span></Label>
                                        <Input value={credForm.subscriptionId || ''} onChange={(e) => setCredForm(f => ({ ...f, subscriptionId: e.target.value }))}
                                            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="font-mono text-sm" />
                                    </div>
                                </div>
                            </>
                        ) : selectedProvider === 'gcp' ? (
                            <>
                                <div className="rounded-lg border bg-blue-50/50 dark:bg-blue-950/20 p-3 mb-4">
                                    <p className="text-xs font-medium mb-1">How to get these credentials:</p>
                                    <ol className="text-xs text-muted-foreground space-y-0.5 list-decimal ml-3">
                                        <li>Run the generated <code className="inline-code">setup-gcp.sh</code> script</li>
                                        <li>It creates a Service Account and outputs a JSON key file</li>
                                        <li>Or create manually: GCP Console → IAM → Service Accounts → Create</li>
                                    </ol>
                                    <p className="text-xs text-muted-foreground mt-1.5">
                                        <strong>Required roles:</strong> Editor, Security Admin, Compute Network Admin
                                    </p>
                                </div>
                                <div className="space-y-3">
                                    <div>
                                        <Label className="text-xs">Project ID <span className="text-muted-foreground">(GCP project to deploy into)</span></Label>
                                        <Input value={credForm.projectId || ''} onChange={(e) => setCredForm(f => ({ ...f, projectId: e.target.value }))}
                                            placeholder="my-project-123" className="font-mono text-sm" />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Service Account Key <span className="text-muted-foreground">(full JSON key file contents)</span></Label>
                                        <textarea value={credForm.serviceAccountKey || ''} onChange={(e) => setCredForm(f => ({ ...f, serviceAccountKey: e.target.value }))}
                                            placeholder='Paste the entire JSON key file contents here...'
                                            className="w-full h-24 rounded-md border bg-background px-3 py-2 font-mono text-xs resize-none" />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Region <span className="text-muted-foreground">(optional — default deployment region)</span></Label>
                                        <Input value={credForm.defaultRegion || ''} onChange={(e) => setCredForm(f => ({ ...f, defaultRegion: e.target.value }))}
                                            placeholder="europe-west2" className="text-sm" />
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="rounded-lg border bg-blue-50/50 dark:bg-blue-950/20 p-3 mb-4">
                                    <p className="text-xs font-medium mb-1">How to get these credentials:</p>
                                    <ol className="text-xs text-muted-foreground space-y-0.5 list-decimal ml-3">
                                        <li>Run the generated <code className="inline-code">setup-aws.sh</code> script</li>
                                        <li>It creates an IAM user for Terraform and outputs access keys</li>
                                        <li>Or create manually: AWS Console → IAM → Users → Create user → Create access key</li>
                                    </ol>
                                    <p className="text-xs text-muted-foreground mt-1.5">
                                        <strong>Required permissions:</strong> AdministratorAccess (for test) or EC2, RDS, VPC, S3, IAM, KMS, CloudWatch
                                    </p>
                                </div>
                                <div className="space-y-3">
                                    <div>
                                        <Label className="text-xs">Access Key ID <span className="text-muted-foreground">(IAM user access key for Terraform)</span></Label>
                                        <Input value={credForm.accessKeyId || ''} onChange={(e) => setCredForm(f => ({ ...f, accessKeyId: e.target.value }))}
                                            placeholder="AKIA..." className="font-mono text-sm" />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Secret Access Key <span className="text-muted-foreground">(IAM user secret key)</span></Label>
                                        <Input type="password" value={credForm.secretAccessKey || ''} onChange={(e) => setCredForm(f => ({ ...f, secretAccessKey: e.target.value }))}
                                            placeholder="••••••••" className="font-mono text-sm" />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Default Region <span className="text-muted-foreground">(optional — where to deploy resources)</span></Label>
                                        <Input value={credForm.defaultRegion || ''} onChange={(e) => setCredForm(f => ({ ...f, defaultRegion: e.target.value }))}
                                            placeholder="eu-west-2" className="text-sm" />
                                    </div>
                                </div>
                            </>
                        )}

                        <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 mt-3">
                            <AlertTriangle className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                            <span className="text-xs text-yellow-700 dark:text-yellow-400">
                                This will create <strong>real cloud resources</strong> that incur costs. Credentials are embedded in the deploy script for this session only and are not stored permanently.
                            </span>
                        </div>

                        <div className="flex justify-end gap-2 mt-4">
                            <Button variant="outline" size="sm" onClick={() => setShowCredentials(false)}>Cancel</Button>
                            <Button size="sm" onClick={() => {
                                // Save creds to Vault (tied to this provider) then immediately trigger deployment
                                saveCredsMutation.mutate(undefined, {
                                    onSuccess: () => {
                                        setShowCredentials(false)
                                        createDeployMutation.mutate()
                                    }
                                })
                            }}
                                disabled={saveCredsMutation.isPending || createDeployMutation.isPending || (
                                    selectedProvider === 'azure' ? (!credForm.clientId || !credForm.clientSecret || !credForm.tenantId || !credForm.subscriptionId) :
                                    selectedProvider === 'gcp' ? (!credForm.projectId || !credForm.serviceAccountKey) :
                                    (!credForm.accessKeyId || !credForm.secretAccessKey)
                                )}>
                                {(saveCredsMutation.isPending || createDeployMutation.isPending)
                                    ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                    : <Rocket className="h-3.5 w-3.5 mr-1.5" />}
                                Run Deployment
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Destroy confirmation */}
            {showConfirmDestroy && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-card rounded-lg border p-6 shadow-lg max-w-md mx-4">
                        <h3 className="text-lg font-semibold mb-2 text-destructive">Destroy Environment</h3>
                        <p className="text-sm text-muted-foreground mb-3">
                            This will download a destroy script. Run it to remove all cloud resources.
                        </p>
                        <div className="text-sm space-y-1 mb-4 text-xs text-muted-foreground">
                            <p>1. Download destroy.sh</p>
                            <p>2. Run <code className="inline-code">bash destroy.sh</code> in the project directory</p>
                            <p>3. Status will be reported back here</p>
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => setShowConfirmDestroy(false)}>Cancel</Button>
                            <Button variant="destructive" size="sm" onClick={() => destroyMutation.mutate()} disabled={destroyMutation.isPending}>
                                <Trash2 className="h-3.5 w-3.5 mr-1.5" />Download Destroy Script
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
